/**
 * Sprint 18, story-084 (Trello GQIogQYh). LogRocket's defaults record every network
 * request/response body and every DOM input verbatim. This module is the single place that
 * configures redaction for the `LogRocket.init` call in `App.tsx`, following the same
 * choke-point convention `errorReporting.ts` already established for reporting.
 *
 * Default-deny posture, applied at three layers:
 *  1. Headers — an explicit allowlist; everything else (including Authorization and the
 *     `Refreshed` bearer-token response header) is stripped, on every route.
 *  2. Operation-category body redaction — a request/response whose GraphQL operation name (or,
 *     for the REST auth endpoints, whose URL) matches one of four sensitive categories has its
 *     ENTIRE body replaced with a redaction marker.
 *  3. Field-level fallback, corrected 2026-09-16 after code review
 *     (`docs/sprints/sprint-18/reviews/story-084-review.md`) found the original denylist
 *     (`SENSITIVE_FIELD_PATTERN`) was default-ALLOW in practice — it had no entry for
 *     `address`/`phone`/`email`/name-shaped keys, so `getClients` and the large majority of this
 *     app's ~100+ GraphQL operations shipped real customer PII to LogRocket unredacted. This is
 *     now a genuine allowlist (`SAFE_FIELD_KEYS`): anything NOT in one of the four whole-body
 *     categories above is redacted UNLESS its key is affirmatively judged safe. A value the
 *     walker cannot classify is redacted — there is no "unknown, pass through" branch.
 *
 * `dom.inputSanitizer: true` (layer 4, wired directly into the `LogRocket.init` call in
 * `App.tsx`, not here) masks every text input by default — no opt-out is added by this story.
 */

import LogRocket from 'logrocket';

// Derived from LogRocket's own `init` signature (rather than a hand-rolled interface with an
// index signature) so `requestSanitizer`/`responseSanitizer` are structurally exact matches for
// `network.requestSanitizer`/`network.responseSanitizer` — no cast needed at the `LogRocket.init`
// call site in `App.tsx`.
type LogRocketInitOptions = NonNullable<Parameters<typeof LogRocket.init>[1]>;
type NetworkOptions = NonNullable<LogRocketInitOptions['network']>;
type RequestSanitizerFn = NonNullable<NetworkOptions['requestSanitizer']>;
type ResponseSanitizerFn = NonNullable<NetworkOptions['responseSanitizer']>;
export type SanitizableRequest = Parameters<RequestSanitizerFn>[0];
export type SanitizableResponse = Parameters<ResponseSanitizerFn>[0];

/**
 * The only headers that survive redaction. Everything else — including `Authorization` and the
 * `Refreshed` bearer-token header the app itself sets (`graphqlClient.ts`'s `responseMiddleware`)
 * — is stripped regardless of which endpoint the request/response hit. Comparison is
 * case-insensitive since HTTP header names are case-insensitive.
 */
const SAFE_HEADERS = new Set([
  'content-type',
  'accept',
  'x-client',
  'x-organization-id',
  'apollo-require-preflight',
]);

/** REST auth endpoints (`services/auth/index.ts`) — not GraphQL, so classified by URL, not operation name. */
const AUTH_URL_MARKER = '/auth/';

/** Login/register/password/token-refresh-shaped GraphQL operations, should any ever be added. */
const AUTH_OPERATIONS = new Set(['login', 'register', 'refreshtoken', 'changepassword', 'resetpassword']);

/** NMI/payment-method mutations and queries. */
const PAYMENT_OPERATIONS = new Set([
  'addpaymentmethod',
  'updatepaymentmethod',
  'deletepaymentmethod',
  'setdefaultpaymentmethod',
  'getpaymentmethods',
  'mypaymentaccount',
  'connectnmiaccount',
  'disconnectnmiaccount',
  'createorganizationwithpayment',
]);

/**
 * Any budget mutation/query whose variables carry costPerUnit/pricePerUnit/line-item amounts —
 * also the required registry for any operation whose response uses the bare key `total` to mean a
 * real dollar amount rather than a pagination count, since `total` is allowlisted in
 * `SAFE_FIELD_KEYS` for pagination shape and the field-level fallback cannot disambiguate the two
 * meanings of that key name (see re-review 2026-09-16,
 * `docs/sprints/sprint-18/reviews/story-084-review.md`). The invoice/standalone-invoice operations
 * below all return `INVOICE_FIELDS`/`STANDALONE_INVOICE_FIELDS`
 * (`invoice.queries.tsx`/`standaloneInvoice.queries.tsx`), where `total` is the invoice's actual
 * billed amount.
 */
const BUDGET_COST_OPERATIONS = new Set([
  'createbudgetrequest',
  'respondtobudgetrequest',
  'acceptbudgetrequest',
  'rejectbudgetrequest',
  'cancelbudgetrequest',
  'acceptbudgetproposal',
  'rejectbudgetproposal',
  'createcounterproposal',
  'paginatedbudgetrequestsbyorganization',
  'availablebudgetitemsforinvoice',
  'getbudgetproposalconversation',
  'getbudgetrequestdetails',
  'getbudgetrequestsbytaskid',
  'getpendingbudgetrequests',
  'getprojectbudgetsummary',
  'createinvoicedraft',
  'deleteinvoicedraft',
  'invoice',
  'invoicesbyproject',
  'paginatedinvoices',
  'projectinvoicependingsummary',
  'refreshinvoicestatus',
  'resendinvoiceemail',
  'sendinvoice',
  'taskinvoicependingsummary',
  'updateinvoicedraft',
  'voidinvoice',
  'createandsendstandaloneinvoice',
  'projectsforstandaloneinvoice',
]);

/** User/employee mutations and queries carrying PII. */
const EMPLOYEE_OPERATIONS = new Set([
  'createemployee',
  'updateemployee',
  'deleteemployee',
  'getemployees',
  'getemployeesfiltered',
  'createuser',
  'updateotheruser',
  'updateownuser',
  'updateuserorganizationrole',
  'updateuserorganizationstatus',
  'updateuserrole',
  'disableuser',
  'enableuser',
  'getpaginatedusers',
  'getuserbyemail',
  'getusers',
  'getuserorganizations',
]);

/**
 * Field-level fallback ALLOWLIST applied to every field NOT already whole-body redacted above.
 * Corrected 2026-09-16 (see `docs/architecture.md`'s story-084 section and Known Deviations &
 * Debt entry): every key is DENY unless it affirmatively matches one of the allow rules below —
 * this is an inversion of the original denylist, which had no entry for
 * `address`/`phone`/`email`/name-shaped keys.
 *
 * Exact-match keys (case-sensitive, matching GraphQL's own camelCase convention). Deliberately
 * does NOT include money/quantity-shaped keys (`cost`, `price`, `amount`, `budget`, `quantity`,
 * `wage`, `salary`) or any free-text key (`name`, `address`, `phone`, `email`, `notes`,
 * `description`, `comment`, `message`, `title`, personal-name-shaped keys) — those redact by
 * omission, which is the entire point of the inversion.
 */
const SAFE_FIELD_KEYS = new Set<string>([
  '__typename',
  'id',
  'status',
  'type',
  'role',
  'state',
  'category',
  'priority',
  'severity',
  'billingPeriod',
  'deliveryStatus',
  'captureChannel',
  'incidentType',
  'documentType',
  'crewType',
  'equipmentType',
  'materialSource',
  'reservationOutcome',
  'paymentMethodType',
  'organizationStatus',
  'subscriptionType',
  'periodStart',
  'periodEnd',
  'actionDate',
  'total',
  'totalCount',
  'totalPages',
  'count',
  'hasMore',
  'hasNextPage',
  'hasPreviousPage',
  'cursor',
  'limit',
  'offset',
  'page',
  'pageSize',
  'success',
  'redacted',
  'operationName',
]);

/**
 * Suffix-match allow rule (case-sensitive, exact terminal substring): identifiers (`Id`/`Ids`),
 * timestamps (`At`), and dates (`Date`) survive by shape rather than by an exhaustive exact-match
 * list, since new `*Id`/`*At`/`*Date` fields are added to the schema constantly.
 */
const SAFE_FIELD_SUFFIXES = ['Id', 'Ids', 'At', 'Date'];

/**
 * Checked before the exact-match/suffix allow rules above: a key that would otherwise match an
 * allow rule by shape but is known to carry sensitive data anyway goes here, not into a weakened
 * allow rule. `hireDate` (ORCH-18-6) is the real instance of this pattern in this codebase: it
 * would otherwise match the `Date` suffix rule, and it appears in `project.queries.tsx` and
 * `crew.queries.tsx` — operations that are NOT in `EMPLOYEE_OPERATIONS`, so the field walker (not
 * whole-body redaction) is what governs it. `birthDate` remains the illustrative hypothetical from
 * this module's own doc comment; no such field exists in this codebase today.
 */
const DENY_OVERRIDE = new Set<string>(['hireDate']);

const REDACTED_VALUE = '[REDACTED]';

/**
 * GraphQL envelope keys are structural, not data fields, and are never allowlist-checked
 * themselves: `query`/`operationName` are copied through verbatim (field-selection text and the
 * operation name carry no data values), while `variables`/`data`/`errors`/`extensions` are
 * containers the walker steps directly into, applying the per-key allow/deny rule to everything
 * nested inside.
 */
const ENVELOPE_PASSTHROUGH_KEYS = new Set(['query', 'operationName']);
const ENVELOPE_CONTAINER_KEYS = new Set(['variables', 'data', 'errors', 'extensions']);

/**
 * Classifies a single key/value pair as ALLOW or DENY. DENY_OVERRIDE is checked first; then the
 * exact-match and suffix-match key-shape rules; then the one allowance not based on key name at
 * all — a boolean or null value carries no free-text or identifying content by construction. A
 * value the walker cannot classify falls through to DENY: classification is DENY-by-default with
 * narrowly-scoped exceptions, so anything not affirmatively matched is DENY by construction —
 * there is no "unknown, pass through" branch.
 */
const isFieldAllowed = (key: string, value: unknown): boolean => {
  if (DENY_OVERRIDE.has(key)) return false;
  if (SAFE_FIELD_KEYS.has(key)) return true;
  if (SAFE_FIELD_SUFFIXES.some((suffix) => key.endsWith(suffix))) return true;
  if (typeof value === 'boolean' || value === null) return true;
  return false;
};

/** Same pattern `errorReporting.ts`'s own `getOperationName` already uses — reused, not reinvented. */
const getOperationName = (query?: string): string | undefined =>
  query?.match(/\b(?:query|mutation|subscription)\s+(\w+)/)?.[1];

/** Extracts the GraphQL operation name straight out of a request/response body string, if any. */
const extractOperationName = (body?: string): string | undefined => {
  if (!body) return undefined;
  try {
    const parsed = JSON.parse(body);
    return getOperationName(parsed?.query);
  } catch {
    return getOperationName(body);
  }
};

type SensitiveCategory = 'auth' | 'payment' | 'budget-cost' | 'employee' | null;

/** Determines which (if any) of the four sensitive categories this request/response belongs to. */
export const classifyOperation = (url: string | undefined, body: string | undefined): SensitiveCategory => {
  if (url?.includes(AUTH_URL_MARKER)) return 'auth';

  const operationName = extractOperationName(body)?.toLowerCase();
  if (!operationName) return null;

  if (AUTH_OPERATIONS.has(operationName)) return 'auth';
  if (PAYMENT_OPERATIONS.has(operationName)) return 'payment';
  if (BUDGET_COST_OPERATIONS.has(operationName)) return 'budget-cost';
  if (EMPLOYEE_OPERATIONS.has(operationName)) return 'employee';
  return null;
};

/** Strips every header not on the `SAFE_HEADERS` allowlist. */
export const sanitizeHeaders = (
  headers: { [key: string]: string | null | undefined } | undefined,
): { [key: string]: string | null | undefined } => {
  if (!headers) return {};
  const result: { [key: string]: string | null | undefined } = {};
  for (const [key, value] of Object.entries(headers)) {
    if (SAFE_HEADERS.has(key.toLowerCase())) {
      result[key] = value;
    }
  }
  return result;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** An array is "of objects" if at least one element is itself an object or array (vs. a bare scalar array). */
const isArrayOfEntities = (value: unknown[]): boolean => value.some((el) => isPlainObject(el) || Array.isArray(el));

/**
 * Walks an already-unwrapped value (i.e. NOT the top-level envelope — see `sanitizeFieldsBelowEnvelope`
 * below for that), re-applying `isFieldAllowed` fresh at every level. This is what stops a
 * safe-looking parent key from smuggling an unredacted nested payload: ALLOW means "do not
 * blanket-redact at this level," never "trust this subtree."
 *
 * - An array is walked element-by-element under this same rule: an array of objects has each
 *   object's own keys classified independently, with NO INHERITANCE from the array's own owning
 *   key — a list of sub-entities (e.g. a client's `projects`) is inspected field-by-field the same
 *   way the top-level object is, even though `projects` itself isn't an allowlisted key, because
 *   the individual project fields (`projectId` survives, `address` doesn't) are what matters, not
 *   the collection's name. A bare array of scalars has no per-element key to classify, so it falls
 *   back to its OWNING key's own verdict (kept only if that key was itself allowed).
 * - An object has each of its own keys classified independently: DENY replaces the entire value
 *   (scalar or object) with `[REDACTED]` and does not recurse further into it; ALLOW keeps a scalar
 *   as-is, and re-walks an unexpectedly-nested object rather than trusting it.
 * - Any other scalar is returned as-is (it only reaches this branch already having been judged
 *   ALLOW, or as an array element with no key of its own).
 */
const walkFields = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(walkFields);
  }
  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {};
    for (const [key, fieldValue] of Object.entries(value)) {
      if (Array.isArray(fieldValue)) {
        result[key] = isArrayOfEntities(fieldValue)
          ? fieldValue.map(walkFields)
          : isFieldAllowed(key, fieldValue)
            ? fieldValue
            : REDACTED_VALUE;
      } else if (!isFieldAllowed(key, fieldValue)) {
        result[key] = REDACTED_VALUE;
      } else if (isPlainObject(fieldValue)) {
        result[key] = walkFields(fieldValue);
      } else {
        result[key] = fieldValue;
      }
    }
    return result;
  }
  return value;
};

/**
 * Applies the envelope rule at the top level: `query`/`operationName` pass through verbatim,
 * `variables`/`data`/`errors`/`extensions` are stepped into directly (their own key names are
 * never allowlist-checked) and then walked field-by-field, and any other top-level key (an
 * envelope shape this app doesn't actually send, or a non-GraphQL JSON body) is classified like
 * any other field — there is no structural assumption beyond the six named envelope keys.
 */
const sanitizeFieldsBelowEnvelope = (parsed: unknown): unknown => {
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return walkFields(parsed);
  }
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (ENVELOPE_PASSTHROUGH_KEYS.has(key)) {
      result[key] = value;
    } else if (ENVELOPE_CONTAINER_KEYS.has(key)) {
      result[key] = walkFields(value);
    } else if (!isFieldAllowed(key, value)) {
      result[key] = REDACTED_VALUE;
    } else if (Array.isArray(value) || (value !== null && typeof value === 'object')) {
      result[key] = walkFields(value);
    } else {
      result[key] = value;
    }
  }
  return result;
};

const wholeBodyRedactionMarker = (operationName: string | undefined): string =>
  JSON.stringify({ operationName: operationName ?? 'unknown', redacted: true });

/**
 * Applies the operation-category / field-level-fallback body redaction described in the module
 * doc comment above. Returns the original body unchanged only if it isn't present. A body that
 * isn't parseable JSON is a shape the walker cannot classify field-by-field, so — per the "a value
 * the walker cannot classify is redacted" rule — it is redacted outright rather than assumed safe
 * just because it wasn't JSON (LogRocket bodies here are always JSON from this app's own
 * `graphql-request`/`axios` calls, so this branch is defensive, not the common path).
 */
export const sanitizeBody = (url: string | undefined, body: string | undefined): string | undefined => {
  if (!body) return body;

  const category = classifyOperation(url, body);
  const operationName = extractOperationName(body);

  if (category) {
    return wholeBodyRedactionMarker(operationName);
  }

  try {
    const parsed = JSON.parse(body);
    return JSON.stringify(sanitizeFieldsBelowEnvelope(parsed));
  } catch {
    return REDACTED_VALUE;
  }
};

/** `network.requestSanitizer` for `LogRocket.init`. */
export const requestSanitizer: RequestSanitizerFn = (request) => ({
  ...request,
  headers: sanitizeHeaders(request.headers),
  body: sanitizeBody(request.url, request.body),
});

/** `network.responseSanitizer` for `LogRocket.init`. */
export const responseSanitizer: ResponseSanitizerFn = (response) => ({
  ...response,
  headers: sanitizeHeaders(response.headers),
  body: sanitizeBody(response.url, response.body),
});

/**
 * Sprint 47: `/setup?token=...` carries a one-time account-setup token in the query string.
 * LogRocket records the page URL, so the `token` value is replaced before it leaves the browser
 * (wired as `browser.urlSanitizer` in `App.tsx`). Every other parameter and the fragment survive.
 */
export const urlSanitizer = (url: string): string => url.replace(/([?&]token=)[^&#]*/gi, `$1${REDACTED_VALUE}`);
